import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@1trading/react';
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, type TooltipProps, XAxis, YAxis } from 'recharts';
import { AXIS_TICK, CURSOR_FILL, GRID_STROKE, TOOLTIP_CONTENT_STYLE } from '@/components/charts/recharts-theme';
import { CROSSING_COLOR, EVENTS_COLOR, formatDayLabel } from '@/features/abuse/abuse-meta';
import type { AbuseSummary } from '@/services/api/abuse';

const EVENTS_CHART_HEIGHT = 260;
const STRIP_HEIGHT = 32;
const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 };
const STRIP_MARGIN = { top: 4, right: 8, bottom: 4, left: 0 };
const SYNC_ID = 'abuse-daily-trend';
const Y_AXIS_WIDTH = 40;
const MAX_BAR_SIZE = 24;
const PILL_HEIGHT = 18;
const PILL_PADDING = 7;
const PILL_DIGIT_WIDTH = 7;
// Dark ink on the warning fill in both themes; white on amber fails contrast
const PILL_TEXT_COLOR = '#1a1a1a';
const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];

interface SummaryProps {
  summary: AbuseSummary | undefined;
  isLoading: boolean;
}

const TrendLegend = () => (
  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="size-2.5 rounded-sm" style={{ backgroundColor: EVENTS_COLOR }} />
      Events (accounts and API keys)
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="h-2.5 w-4 rounded-full" style={{ backgroundColor: CROSSING_COLOR }} />
      Threshold crossings
    </span>
  </div>
);
TrendLegend.displayName = 'TrendLegend';

interface StripCellProps {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: number | string;
}

/** One cell per day, centred on that day's band; a count pill where crossings happened, a faint dot elsewhere so the row reads as a timeline */
const CrossingStripCell = ({ x, y, width, height, value }: StripCellProps) => {
  const crossings = Number(value);
  const cx = Number(x) + Number(width) / 2;
  const cy = Number(y) + Number(height) / 2;

  if (!crossings) return <circle cx={cx} cy={cy} fill="hsl(var(--muted-foreground))" opacity={0.35} r={1.5} />;

  const pillWidth = PILL_PADDING * 2 + String(crossings).length * PILL_DIGIT_WIDTH;

  return (
    <g>
      <rect fill={CROSSING_COLOR} height={PILL_HEIGHT} rx={PILL_HEIGHT / 2} width={pillWidth} x={cx - pillWidth / 2} y={cy - PILL_HEIGHT / 2} />
      <text dominantBaseline="central" fill={PILL_TEXT_COLOR} fontSize={11} fontWeight={600} textAnchor="middle" x={cx} y={cy}>
        {crossings}
      </text>
    </g>
  );
};
CrossingStripCell.displayName = 'CrossingStripCell';

type DailyRow = AbuseSummary['daily'][number];

const TrendTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  const day = payload?.[0]?.payload as DailyRow | undefined;

  if (!active || !day) return null;

  return (
    <div className="flex flex-col gap-1 px-3 py-2 text-xs" style={TOOLTIP_CONTENT_STYLE}>
      <span className="font-medium">{formatDayLabel(day.date)}</span>
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden className="size-2 rounded-sm" style={{ backgroundColor: EVENTS_COLOR }} />
        {day.events} events
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: CROSSING_COLOR }} />
        {day.crossings} threshold crossing{day.crossings === 1 ? '' : 's'}
      </span>
    </div>
  );
};
TrendTooltip.displayName = 'TrendTooltip';

/**
 * Crossings get their own strip under the events chart, sharing its days and hover, rather than a
 * second y-axis: a scale of 0-3 against 0-20 events would draw one crossing as tall as the busiest day.
 */
export const TrendCard = ({ summary, isLoading }: SummaryProps) => (
  <Card>
    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
      <CardTitle>Daily trend</CardTitle>
      <TrendLegend />
    </CardHeader>
    <CardContent className="flex flex-col gap-1">
      {isLoading || !summary ? (
        <Skeleton className="h-[300px] w-full" />
      ) : (
        <>
          <ResponsiveContainer height={EVENTS_CHART_HEIGHT} width="100%">
            <BarChart data={summary.daily} margin={CHART_MARGIN} syncId={SYNC_ID}>
              <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" minTickGap={24} tick={AXIS_TICK} tickFormatter={formatDayLabel} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} width={Y_AXIS_WIDTH} />
              <Tooltip content={<TrendTooltip />} cursor={CURSOR_FILL} />
              <Bar dataKey="events" fill={EVENTS_COLOR} maxBarSize={MAX_BAR_SIZE} name="Events" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
          <ResponsiveContainer height={STRIP_HEIGHT} width="100%">
            <BarChart accessibilityLayer data={summary.daily.map((day) => ({ ...day, lane: 1 }))} margin={STRIP_MARGIN} syncId={SYNC_ID}>
              <XAxis dataKey="date" hide />
              {/* Visible but blank: a hidden axis reserves no width, which would shift the strip's days off the bars above */}
              <YAxis axisLine={false} domain={[0, 1]} tick={false} tickLine={false} width={Y_AXIS_WIDTH} />
              <Tooltip content={<TrendTooltip />} cursor={CURSOR_FILL} />
              <Bar dataKey="lane" fill="transparent" isAnimationActive={false} name="Crossings">
                <LabelList content={<CrossingStripCell />} dataKey="crossings" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </CardContent>
  </Card>
);
TrendCard.displayName = 'TrendCard';
